import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, ArrowLeft } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { brl, fmtDate, statusLabel, statusColor } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const EMPTY_EQ = { type: "", brand: "", model: "", serial: "", capacity: "", refrigerant: "", year: "", location: "", notes: "", photos: [] };
const EQ_TYPES = ["Ar-condicionado split", "Câmara fria", "Freezer", "Balcão refrigerado", "Expositor", "Chiller", "Bomba de calor", "Refrigerador comercial", "Outro"];
const REFRIGERANTS = ["R22", "R410A", "R32", "R404A", "R134a", "R290", "R600a", "Outro"];

export default function CustomerDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [eq, setEq] = useState(EMPTY_EQ);

  const load = () => api.get(`/customers/${id}`).then((r) => setData(r.data)).catch(() => toast.error("Cliente não encontrado."));
  useEffect(() => { load(); }, [id]);

  const setE = (k) => (e) => setEq({ ...eq, [k]: e.target.value });

  const uploadEqPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("context", "equipamentos");
      const { data } = await api.post("/files/upload", fd);
      setEq((cur) => ({ ...cur, photos: [...(cur.photos || []), data.url] }));
      toast.success("Foto anexada ao equipamento.");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const saveEq = async (e) => {
    e.preventDefault();
    try {
      await api.post("/equipment", { ...eq, customer_id: id });
      toast.success("Equipamento cadastrado!");
      setOpen(false);
      setEq(EMPTY_EQ);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const removeEq = async (eid) => {
    if (!window.confirm("Excluir este equipamento?")) return;
    await api.delete(`/equipment/${eid}`);
    load();
  };

  if (!data) return <div className="py-20 text-center text-slate-400">Carregando...</div>;

  return (
    <div className="space-y-5 animate-in-up" data-testid="customer-detail">
      <Link to="/app/clientes" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800" data-testid="back-customers">
        <ArrowLeft className="h-4 w-4" /> Clientes
      </Link>

      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="text-2xl font-display">{data.name}</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-slate-600 space-y-1">
          {data.phone && <p>Telefone: {data.phone}</p>}
          {data.whatsapp && <p>WhatsApp: {data.whatsapp}</p>}
          {data.email && <p>E-mail: {data.email}</p>}
          {data.address && <p>Endereço: {data.address}</p>}
          {data.doc && <p>CNPJ/CPF: {data.doc}</p>}
          {data.notes && <p className="text-slate-500 italic">{data.notes}</p>}
        </CardContent>
      </Card>

      <Tabs defaultValue="equipamentos">
        <TabsList className="w-full justify-start overflow-x-auto">
          <TabsTrigger value="equipamentos" data-testid="tab-equipment">Equipamentos ({data.equipment.length})</TabsTrigger>
          <TabsTrigger value="orcamentos" data-testid="tab-quotes">Orçamentos ({data.quotes.length})</TabsTrigger>
          <TabsTrigger value="os" data-testid="tab-os">OS ({data.work_orders.length})</TabsTrigger>
          <TabsTrigger value="manutencao" data-testid="tab-maint">Manutenções ({data.maintenance.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="equipamentos" className="mt-4 space-y-3">
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="h-12 bg-sky-600 hover:bg-sky-700 text-white" data-testid="new-equipment-btn">
                <Plus className="h-4 w-4 mr-2" /> Adicionar equipamento
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>Novo equipamento</DialogTitle></DialogHeader>
              <form onSubmit={saveEq} className="space-y-3">
                <div className="space-y-1.5">
                  <Label>Tipo *</Label>
                  <Select value={eq.type} onValueChange={(v) => setEq({ ...eq, type: v })} required>
                    <SelectTrigger className="h-12" data-testid="eq-type"><SelectValue placeholder="Selecione" /></SelectTrigger>
                    <SelectContent>{EQ_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label>Fabricante</Label><Input className="h-12" data-testid="eq-brand" value={eq.brand} onChange={setE("brand")} /></div>
                  <div className="space-y-1.5"><Label>Modelo</Label><Input className="h-12" data-testid="eq-model" value={eq.model} onChange={setE("model")} /></div>
                  <div className="space-y-1.5"><Label>Nº de série</Label><Input className="h-12" data-testid="eq-serial" value={eq.serial} onChange={setE("serial")} /></div>
                  <div className="space-y-1.5"><Label>Capacidade (BTUs)</Label><Input className="h-12" data-testid="eq-capacity" value={eq.capacity} onChange={setE("capacity")} /></div>
                  <div className="space-y-1.5">
                    <Label>Refrigerante</Label>
                    <Select value={eq.refrigerant} onValueChange={(v) => setEq({ ...eq, refrigerant: v })}>
                      <SelectTrigger className="h-12" data-testid="eq-refrigerant"><SelectValue placeholder="Selecione" /></SelectTrigger>
                      <SelectContent>{REFRIGERANTS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5"><Label>Ano</Label><Input className="h-12" data-testid="eq-year" value={eq.year} onChange={setE("year")} /></div>
                </div>
                <div className="space-y-1.5"><Label>Localização</Label><Input className="h-12" data-testid="eq-location" value={eq.location} onChange={setE("location")} placeholder="Ex: cozinha, depósito" /></div>
                <div className="space-y-1.5"><Label>Observações</Label><Input className="h-12" data-testid="eq-notes" value={eq.notes} onChange={setE("notes")} /></div>
                <div className="space-y-1.5">
                  <Label>Fotos do equipamento</Label>
                  <Input type="file" accept="image/*" className="h-12" onChange={uploadEqPhoto} data-testid="eq-photo-input" />
                  {(eq.photos || []).length > 0 && (
                    <div className="flex gap-2 flex-wrap pt-1">
                      {eq.photos.map((u, i) => (
                        <img key={i} src={`${process.env.REACT_APP_BACKEND_URL}${u}`} alt={`Foto ${i + 1}`} className="h-16 w-16 rounded-lg object-cover border border-slate-200" data-testid={`eq-photo-preview-${i}`} />
                      ))}
                    </div>
                  )}
                </div>
                <Button type="submit" className="w-full h-12 bg-sky-600 hover:bg-sky-700 text-white" data-testid="eq-save-btn">Salvar equipamento</Button>
              </form>
            </DialogContent>
          </Dialog>

          {data.equipment.length === 0 && <p className="text-sm text-slate-500">Nenhum equipamento cadastrado.</p>}
          {data.equipment.map((e) => (
            <Card key={e.id} className="border-slate-200" data-testid={`equipment-card-${e.id}`}>
              <CardContent className="p-4 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-semibold">{[e.type, e.brand, e.model].filter(Boolean).join(" · ")}</p>
                  <p className="text-sm text-slate-500">
                    {[e.capacity && `${e.capacity} BTUs`, e.refrigerant, e.location].filter(Boolean).join(" · ")}
                  </p>
                  {(e.photos || []).length > 0 && (
                    <div className="flex gap-1.5 mt-2">
                      {e.photos.slice(0, 4).map((u, i) => (
                        <img key={i} src={`${process.env.REACT_APP_BACKEND_URL}${u}`} alt="" className="h-10 w-10 rounded object-cover border border-slate-200" data-testid={`equipment-photo-${e.id}-${i}`} />
                      ))}
                    </div>
                  )}
                </div>
                <Button variant="ghost" size="icon" className="h-11 w-11 text-red-500 hover:bg-red-50 hover:text-red-700"
                        onClick={() => removeEq(e.id)} data-testid={`equipment-delete-${e.id}`}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="orcamentos" className="mt-4 space-y-2">
          {data.quotes.length === 0 && <p className="text-sm text-slate-500">Nenhum orçamento para este cliente.</p>}
          {data.quotes.map((q) => (
            <Link key={q.id} to={`/app/orcamentos/${q.id}`} className="flex items-center justify-between border border-slate-200 rounded-lg p-3.5 bg-white hover:bg-slate-50" data-testid={`cust-quote-${q.number}`}>
              <div>
                <p className="text-sm font-medium">{q.number} · {q.service_type}</p>
                <p className="text-xs text-slate-500">{fmtDate(q.created_at)}</p>
              </div>
              <div className="text-right">
                <p className="font-mono-num text-sm font-semibold">{brl(q.calc?.preco_final)}</p>
                <Badge variant="outline" className={`text-[10px] ${statusColor[q.status]}`}>{statusLabel[q.status]}</Badge>
              </div>
            </Link>
          ))}
        </TabsContent>

        <TabsContent value="os" className="mt-4 space-y-2">
          {data.work_orders.length === 0 && <p className="text-sm text-slate-500">Nenhuma OS para este cliente.</p>}
          {data.work_orders.map((o) => (
            <div key={o.id} className="flex items-center justify-between border border-slate-200 rounded-lg p-3.5 bg-white" data-testid={`cust-os-${o.number}`}>
              <div>
                <p className="text-sm font-medium">{o.number}</p>
                <p className="text-xs text-slate-500">{fmtDate(o.scheduled_date)} · {o.problem || "-"}</p>
              </div>
              <Badge variant="outline" className={`text-[10px] ${statusColor[o.status]}`}>{statusLabel[o.status]}</Badge>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="manutencao" className="mt-4 space-y-2">
          {data.maintenance.length === 0 && <p className="text-sm text-slate-500">Nenhuma manutenção programada.</p>}
          {data.maintenance.map((m) => (
            <div key={m.id} className="flex items-center justify-between border border-slate-200 rounded-lg p-3.5 bg-white" data-testid={`cust-maint-${m.id}`}>
              <div>
                <p className="text-sm font-medium">A cada {m.frequency_months} meses</p>
                <p className="text-xs text-slate-500">Próxima: {fmtDate(m.next_date)}</p>
              </div>
            </div>
          ))}
          <Link to="/app/manutencao">
            <Button variant="outline" className="h-11" data-testid="go-maintenance-btn">Gerenciar manutenções</Button>
          </Link>
        </TabsContent>
      </Tabs>
    </div>
  );
}
